import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Loader2, Sparkles, Shield, HardHat, Package, CheckCircle2 } from 'lucide-react';
import { useCompleteOnboarding, useSkipOnboarding } from '@/hooks/use-onboarding';

const MODULE_CONTENT: Record<string, { icon: React.ReactNode; label: string; items: string[] }> = {
  inventory: {
    icon: <Package className="h-5 w-5" />,
    label: 'Equipment categories (H3Gear)',
    items: ['Harness', 'Lanyard', 'Rope', 'Descender', 'Ascender', 'Helmet', 'Carabiner'],
  },
  safety: {
    icon: <Shield className="h-5 w-5" />,
    label: 'JSA templates (H3Safe)',
    items: ['Working at Height', 'Confined Space Entry', 'Hot Work', 'Rigging & Lifting', 'Rope Access'],
  },
  crew: {
    icon: <HardHat className="h-5 w-5" />,
    label: 'Certification checklist (H3Crew)',
    items: ['IRATA Level 1', 'IRATA Level 2', 'IRATA Level 3', 'SPRAT Level 1', 'SPRAT Level 2', 'SPRAT Level 3', 'First Aid', 'Working at Height'],
  },
};

export function StarterContentStep({
  enabledModules,
  storageKey,
}: {
  enabledModules: string[];
  storageKey: string | null;
}) {
  const navigate = useNavigate();
  const completeOnboarding = useCompleteOnboarding();
  const skipOnboarding = useSkipOnboarding();

  const modulesToShow = enabledModules.filter(m => m in MODULE_CONTENT);

  const handleConfirm = () => {
    completeOnboarding.mutate(undefined, {
      onSuccess: () => {
        if (storageKey) localStorage.removeItem(storageKey);
        navigate('/dashboard', { replace: true });
      },
    });
  };

  const handleLater = async () => {
    try {
      await skipOnboarding.mutateAsync();
      navigate('/dashboard', { replace: true });
    } catch {
      return;
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Sparkles className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          Your starter content is ready
        </h1>
        <p className="mt-2 text-gray-500">
          We&apos;ve pre-loaded templates and categories to help you hit the ground running.
          You can edit or remove any of these at any time.
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {modulesToShow.map(moduleKey => {
          const content = MODULE_CONTENT[moduleKey];
          return (
            <Card key={moduleKey}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="text-blue-600">{content.icon}</span>
                  {content.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {content.items.map(item => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => void handleLater()}
          disabled={completeOnboarding.isPending || skipOnboarding.isPending}
        >
          {skipOnboarding.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I&apos;ll explore this later
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={completeOnboarding.isPending || skipOnboarding.isPending}
          className="bg-green-600 hover:bg-green-700"
        >
          {completeOnboarding.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Looks great, finish setup
        </Button>
      </div>
      {skipOnboarding.isError && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          Failed to skip setup. Please try again.
        </p>
      )}
    </div>
  );
}
